import { fetchUtils, HttpError } from 'react-admin';
import * as Sentry from '@sentry/react';

export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// A support session belongs to one tab: its token lives in that tab's
// sessionStorage, so the platform admin's other tabs, and their own session in
// localStorage, never change underneath them.
const SUPPORT_TOKEN_KEY = 'supportAuth';

const supportToken = () => {
  try {
    return sessionStorage.getItem(SUPPORT_TOKEN_KEY);
  } catch {
    return null;
  }
};

export const getAuthToken = () => supportToken() ?? localStorage.getItem('auth');
// A support session never renews: when its hour is up it ends.
export const getRefreshToken = () => (supportToken() ? null : localStorage.getItem('refresh'));

export const setAuthTokens = (accessToken: string, refreshToken: string) => {
  localStorage.setItem('auth', accessToken);
  localStorage.setItem('refresh', refreshToken);
};

export const clearAuthTokens = () => {
  localStorage.removeItem('auth');
  localStorage.removeItem('refresh');
  endSupportSession();
};

// What the access token says. For showing and hiding things only: the API
// checks every one of these again on each call.
export type SessionClaims = {
  exp?: number;
  email?: string;
  role?: string;
  demo?: boolean;
  // Set for the addresses in PLATFORM_ADMIN_EMAILS.
  platformAdmin?: boolean;
  // A platform admin signed in to another workspace as its admin.
  supportBy?: number;
  supportTenant?: string;
};

export const decodeJwtPayload = (token: string) => {
  try {
    const [, payload] = token.split('.');
    if (!payload) {
      return null;
    }
    const binary = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    // UTF-8, so a workspace name with an accent survives.
    const decoded = new TextDecoder().decode(Uint8Array.from(binary, (char) => char.charCodeAt(0)));
    return JSON.parse(decoded) as SessionClaims;
  } catch {
    return null;
  }
};

export const getSessionClaims = () => {
  const token = getAuthToken();
  return token ? decodeJwtPayload(token) : null;
};

export const isDemoSession = () => Boolean(getSessionClaims()?.demo);

export const isSupportSession = () => Boolean(getSessionClaims()?.supportBy);

export const isPlatformSession = () => {
  const claims = getSessionClaims();
  return Boolean(claims?.platformAdmin && !claims.supportBy);
};

// Signs this tab in to a workspace as its admin. The caller reloads, so nothing
// from the last workspace stays in memory.
export const startSupportSession = (accessToken: string) => {
  sessionStorage.setItem(SUPPORT_TOKEN_KEY, accessToken);
};

// This tab goes back to the platform admin's own session.
export const endSupportSession = () => {
  try {
    sessionStorage.removeItem(SUPPORT_TOKEN_KEY);
  } catch {
    // Storage is off, so there was no support session to end.
  }
};

// Demo sessions have no refresh token (visitors share one user), so renewing one
// means asking for a new session. Throws an HttpError with the status.
export const openDemoSession = async () => {
  const response = await fetch(`${API_BASE_URL}/auth/demo`, { method: 'POST' });
  const data = response.ok ? ((await response.json()) as { access_token?: string }) : {};
  if (!data.access_token) {
    throw new HttpError('The demo is not available', response.status);
  }
  localStorage.setItem('auth', data.access_token);
  localStorage.removeItem('refresh');
  return data.access_token;
};

export const withAuthHeaders = (headers?: HeadersInit) => {
  const next = new Headers(headers);
  const token = getAuthToken();
  if (token) {
    next.set('Authorization', `Bearer ${token}`);
  }
  return next;
};

let refreshPromise: Promise<string | null> | null = null;

export const refreshAccessToken = async () => {
  // A support session has no refresh token, so a request refused in one means
  // its hour is up. The workspace's request must never be retried as the
  // platform admin: start over at Platform on a fresh page, and leave this
  // promise pending while the reload takes over.
  if (isSupportSession()) {
    endSupportSession();
    window.location.hash = '#/platform';
    window.location.reload();
    return new Promise<string | null>(() => {});
  }
  const refreshToken = getRefreshToken();
  if (!refreshToken && !isDemoSession()) {
    clearAuthTokens();
    return null;
  }

  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      if (!refreshToken) {
        return await openDemoSession();
      }
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: new Headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ refreshToken })
      });
      if (!response.ok) {
        throw new Error('Refresh failed');
      }
      const data = (await response.json()) as {
        access_token?: string;
        refresh_token?: string;
      };
      if (!data.access_token || !data.refresh_token) {
        throw new Error('Invalid refresh response');
      }
      setAuthTokens(data.access_token, data.refresh_token);
      return data.access_token;
    } catch {
      clearAuthTokens();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

const needsRefresh = (error: unknown) => {
  if (error instanceof HttpError) {
    return error.status === 401 || error.status === 403;
  }
  if (typeof error === 'object' && error && 'status' in error) {
    const status = (error as { status?: number }).status;
    return status === 401 || status === 403;
  }
  return false;
};

const captureHttpError = (error: unknown, url: string, options?: fetchUtils.Options) => {
  if (!(error instanceof HttpError)) {
    return;
  }
  if (error.status < 500) {
    return;
  }
  Sentry.captureException(error, {
    tags: { status: String(error.status) },
    extra: {
      url,
      method: options?.method,
      response: error.body
    }
  });
};

const normalizeHttpError = (error: unknown) => {
  if (!(error instanceof HttpError)) {
    return error;
  }

  const body = error.body as { message?: string | string[] } | undefined;
  const message = Array.isArray(body?.message)
    ? body?.message.join(', ')
    : (body?.message ?? error.message);

  if (message === error.message) {
    return error;
  }

  return new HttpError(message, error.status, error.body);
};

export const authFetchJson = (url: string, options: fetchUtils.Options = {}) => {
  const headers = withAuthHeaders(options.headers);
  return fetchUtils.fetchJson(url, { ...options, headers }).catch(async (error) => {
    if (!needsRefresh(error)) {
      captureHttpError(error, url, options);
      throw normalizeHttpError(error);
    }
    const token = await refreshAccessToken();
    if (!token) {
      captureHttpError(error, url, options);
      throw normalizeHttpError(error);
    }
    const retryHeaders = withAuthHeaders(options.headers);
    return fetchUtils.fetchJson(url, { ...options, headers: retryHeaders }).catch((retryError) => {
      captureHttpError(retryError, url, options);
      throw normalizeHttpError(retryError);
    });
  });
};

export const authFetch = async (input: RequestInfo | URL, init: RequestInit = {}) => {
  const headers = withAuthHeaders(init.headers);
  let response = await fetch(input, { ...init, headers });
  if (response.status !== 401 && response.status !== 403) {
    return response;
  }

  const token = await refreshAccessToken();
  if (!token) {
    return response;
  }

  const retryHeaders = withAuthHeaders(init.headers);
  response = await fetch(input, { ...init, headers: retryHeaders });
  return response;
};
